import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { MasterMilestoneFindInterface } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';
import { HelpdeskDtoKeyEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk-dto-key.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { EavEntityFormDto, type EavEntityFormInterface } from '@zaoblako/shared/modules/eav/dtos/entity/eav-entity-form.dto';

import * as yup from 'yup';

export interface HelpdeskTaskFormInterface {
  deadline?: Date;
  plannedMin?: number;
  description: string;
  project?: ProjectFindInterface;
  deal?: DealFindInterface;
  client?: ClientFindInterface;
  externalUser?: ExternalUserFindInterface;
  responsible?: UserFindInterface;
  taskType?: HelpdeskTaskTypeFindInterface;
  unit?: UnitFindInterface;
  priority?: HelpdeskTaskPriorityEnum;
  userAuditors?: UserFindInterface[];
  userGroupAuditors?: UserGroupFindInterface[];
  externalUserAuditors?: ExternalUserFindInterface[];
  masterMilestone?: MasterMilestoneFindInterface;
  eavEntity?: EavEntityFormInterface;
}

export const HelpdeskTaskFormDto = SerdeDtoClass.create<HelpdeskTaskFormInterface>(HelpdeskDtoKeyEnum.HELPDESK_TASK_FORM_DTO, {
  deadline: {
    optional: context => !context?.data?.helpdeskRequiredFields?.deadline,
    label: 'Дедлайн',
    serializer: yup.date(),
  },
  plannedMin: {
    optional: context => !context?.data?.helpdeskRequiredFields?.plannedMin,
    sortable: true,
    label: 'Запланированное время',
    serializer: yup.number(),
  },
  description: {
    label: 'Описание',
    serializer: yup
      .string()
      .min(1, DtoMessageEnum.EMPTY_STRING),
  },
  project: {
    optional: context => !context?.data?.helpdeskRequiredFields?.project,
    label: 'Проект',
    serializer: entityLikeSchema,
  },
  deal: {
    optional: context => !context?.data?.helpdeskRequiredFields?.deal,
    label: 'Сделка',
    serializer: entityLikeSchema,
  },
  client: {
    optional: context => !context?.data?.helpdeskRequiredFields?.client,
    label: 'Клиент',
    serializer: entityLikeSchema,
  },
  externalUser: {
    optional: context => !context?.data?.helpdeskRequiredFields?.externalUser,
    label: 'Автор (внешний)',
    serializer: entityLikeSchema,
  },
  responsible: {
    optional: context => !context?.data?.helpdeskRequiredFields?.responsible,
    label: 'Ответственный',
    serializer: entityLikeSchema,
  },
  taskType: {
    optional: context => !context?.data?.helpdeskRequiredFields?.taskType,
    label: 'Тип',
    serializer: entityLikeSchema,
  },
  unit: {
    optional: context => !context?.data?.helpdeskRequiredFields?.unit,
    label: 'БЕ',
    serializer: entityLikeSchema,
  },
  priority: {
    optional: true,
    label: 'Приоритет',
    serializer: yup
      .string()
      .oneOf(Object.values(HelpdeskTaskPriorityEnum)),
  },
  userAuditors: {
    label: 'Аудиторы',
    optional: context => !context?.data?.helpdeskRequiredFields?.userAuditors,
    serializer: yup.array(entityLikeSchema),
  },
  userGroupAuditors: {
    label: 'Группы аудиторов',
    optional: context => !context?.data?.helpdeskRequiredFields?.userGroupAuditors,
    serializer: yup.array(entityLikeSchema),
  },
  externalUserAuditors: {
    label: 'Внешние аудиторы',
    optional: context => !context?.data?.helpdeskRequiredFields?.externalUserAuditors,
    serializer: yup.array(entityLikeSchema),
  },
  masterMilestone: {
    sortable: true,
    optional: true,
    label: 'Мастер-веха',
    serializer: entityLikeSchema,
  },
  eavEntity: {
    optional: true,
    label: 'Дополнительные реквизиты',
    nested: EavEntityFormDto,
    serializer: (_, nested) => nested,
  },
});
